import { useRef, useState } from 'react';

import styled from 'styled-components/macro';
import swal from 'sweetalert';

import {
    Typography, Fab, Tooltip, Checkbox, Chip, Dialog, DialogContent,
    DialogActions, Button, Grid, Popover, CircularProgress
} from '@material-ui/core';
import {
    DeleteForever, LockOpenOutlined, LockOutlined,
    Person, VerifiedUser, Work, AlarmOn
} from '@material-ui/icons';

import LoadingProgress from './LoadingProgress';

import commentsService from '../domain/services/solicitation/comments.service';
import loginService from '../domain/services/login.service';
import { Alert } from '@material-ui/lab';

import CommentBlockedNotice from './comments/commentBlockedNotice';

const SEM_MOTIVO_DECLARADO = 'DESCONHECIDO';

const ChatMessage = styled(Grid)`
    text-align: ${(props) => props.position};
    width: 100%;
    min-width: 0;
`;

const ChatMessageInner = styled.div`
    display: inline-block;
    max-width: 85%;
    min-width: min(240px, 85%);
    text-align: left;

    @media (max-width: 600px) {
        max-width: 95%;
        min-width: min(200px, 95%);
    }
`;

const ChatMessageTime = styled(Typography)`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 4px;
    opacity: 0.8;
    font-size: 11px;
`;

const ChatMessageBubble = styled.div`
    display: block;
    margin-right: auto;
    background: ${(props) => (props.highlighted ? '#eaf3ff' : props.theme.palette.background.paper)};
    color: ${props => props.theme.palette.text.primary};
    border: 1px solid ${props => props.theme.palette.divider};
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 4px;
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;
`;

const ChatHead = styled.div`
    font-weight: 600;
    font-size: 12px;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    min-width: 0;
    overflow-wrap: anywhere;
`;

function ChatMessageComponent({ comment, refreshComments, position = 'left', showRealeaseCustomerPopover, setShowRealeaseCustomerPopover }) {

    const [showConfirmReleaseModal, setShowConfirmReleaseModal] = useState(false);
    const [blockedReason, setBlockedReason] = useState(null);

    const [isReleasingCommentToCustomer, setIsReleasingCommentToCustomer] = useState(false);

    const customerReleaseButtonAnchor = useRef(null);

    const handleCloseConfirmReleaseModal = () => {
        setShowConfirmReleaseModal(false);
    }

    const deleteComments = () => {
        if (loginService.getUserAuthentication().id == comment.senderUserId)
            return (
                <Fab
                    aria-label="Excluir comentário"
                    onClick={checkRemove} size="small" color="default"
                    style={{
                        marginLeft: '10px', height: '23px',
                        minHeight: '20px', minWidth: '20px', width: '23px'
                    }}
                >
                    <DeleteForever style={{ fontSize: '16px' }} />
                </Fab>
            );
    };

    const confirmEnableCommentVisibilityModal = () => {
        return (
            <Dialog
                open={showConfirmReleaseModal} maxWidth="xs" fullWidth
                onClose={(e, reason) => {
                    if (reason !== 'backdropClick' && !isReleasingCommentToCustomer)
                        handleCloseConfirmReleaseModal();
                }}
            >
                <DialogContent>

                    <Typography style={{ fontSize: '1rem', marginTop: '1rem', marginBottom: '1rem' }}>
                        Você está liberando o comentário para o cliente
                    </Typography>

                    <Typography style={{ fontSize: '1rem', marginBottom: '1rem' }}>
                        Deseja continuar?
                    </Typography>

                </DialogContent>

                <DialogActions>

                    <Button
                        variant="contained"
                        onClick={handleCloseConfirmReleaseModal}
                        disabled={isReleasingCommentToCustomer}
                    >
                        Cancelar
                    </Button>

                    <Button
                        variant="contained" color="primary"
                        onClick={handleConfirmEnableComment}
                        disabled={isReleasingCommentToCustomer}
                    >
                        <CircularProgress
                            size={20}
                            style={{ display: isReleasingCommentToCustomer ? 'block' : 'none', marginRight: 10 }}
                        />
                        Confirmar
                    </Button>

                </DialogActions>
            </Dialog>
        )

    }

    const handleClickCommentLock = async (checked) => {

        if (checked) {
            setShowConfirmReleaseModal(true);
            return;
        }

        const isVisibleToCustomer = false;
        update(isVisibleToCustomer)
    }

    const handleConfirmEnableComment = async () => {
        if (isReleasingCommentToCustomer) return;
        try {
            setIsReleasingCommentToCustomer(true);
            await update(true);
            handleCloseConfirmReleaseModal();
            setBlockedReason(null);
        }
        catch (error) {
            handleCloseConfirmReleaseModal();
            setBlockedReason(error?.response?.data?.reason || SEM_MOTIVO_DECLARADO);
        } finally {
            setShowRealeaseCustomerPopover?.(false);
            setIsReleasingCommentToCustomer(false);
        }
    }

    const messageWaitingToShowCustomer = () => {

        if (!loginService.isInternal() || !comment.isWaitingToShowCustomer || comment.isVisibleCustomer)
            return null;

        return (
            <Tooltip title={`
                Aguardando disponibilização para o cliente. O comentário ficará disponível para o cliente 
                quando a solicitação for finalizada ou enviada para Pendências.
            `}>
                <AlarmOn fontSize='14' style={{ color: 'orange', marginBottom: -6, marginRight: 10 }} />
            </Tooltip >
        );
    }

    const enabledMessageToCustomer = () => {

        if (!(loginService.isInternal() && comment.source != 'customer') && !(loginService.isFaciliter() && comment.source == 'faciliter'))
            return null;

        const isChecked = comment.isVisibleCustomer || comment.isWaitingToShowCustomer;

        const tooltipTitle = isChecked ? "Bloquear para o cliente" : "Tornar visível para o cliente";

        return (
            <Tooltip title={tooltipTitle}>
                <Checkbox
                    color="primary"
                    checked={isChecked}
                    onChange={(e) => handleClickCommentLock(e.target.checked)}
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                    icon={<LockOutlined style={{ fontSize: '18px' }} />}
                    checkedIcon={<LockOpenOutlined style={{ fontSize: '18px' }} />}
                    style={{ width: '30px', height: '30px', marginLeft: '5px' }}
                />
            </Tooltip >
        );
    }

    const checkRemove = () =>
        swal({
            title: 'Excluir comentário',
            text: 'Será removido para todos. Realmente deseja excluir?',
            icon: 'warning',
            buttons: {
                confirm: { text: 'Excluir', className: 'MuiButton-containedPrimary' },
                cancel: 'Cancelar',
            },
        }).then((res) => {
            if (res) remove();
        });

    const remove = async () => {
        await commentsService.remove(comment);
        refreshComments();
    };

    const update = async (isVisibleCustomer) => {
        const response = await commentsService.update({ ...comment, isVisibleCustomer });
        if (response?.data?.success === false) {
            const error = new Error('Comment visibility update failed');
            error.response = response;
            throw error;
        }
        refreshComments();
    };

    const getSourceName = (source) => {

        const sources = {
            customer: 'Cliente',
            customerUser: 'Usuário do Cliente',
            internal: 'Interno',
            faciliter: 'Faciliter',
        };

        return sources[source];
    };

    const getSourceIcon = (source) => {

        const sources = {
            customer: <Person />,
            customerUser: <Person />,
            internal: <VerifiedUser />,
            faciliter: <Work />,
        };

        return sources[source];
    };

    return (
        <ChatMessage className='max-w-full' item position={position}>
            <ChatMessageInner>

                <ChatHead>
                    {comment.source == 'faciliter' && loginService.isCustomer() ?
                        "Faciliter" : comment.senderUserName}
                    {loginService.isInternal() && (
                        <Chip
                            icon={getSourceIcon(comment.source)}
                            label={getSourceName(comment.source)}
                            size="small" style={{ marginLeft: 8 }}
                        />
                    )}
                </ChatHead>

                <ChatMessageBubble
                    highlighted={position === 'right'}
                    ref={customerReleaseButtonAnchor}
                    data-comment-id={comment.id}
                >
                    <Typography variant="body2" style={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                        {(comment.description || '').replace(/\\n/g, '\n')}
                    </Typography>
                </ChatMessageBubble>

                {blockedReason && (
                    <CommentBlockedNotice reason={blockedReason} onDismiss={() => setBlockedReason(null)} />
                )}

                <ChatMessageTime variant="body2">
                    {messageWaitingToShowCustomer()}
                    {new Date(comment.created).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })}
                    {deleteComments()}
                    {enabledMessageToCustomer()}
                    {confirmEnableCommentVisibilityModal()}
                </ChatMessageTime>

            </ChatMessageInner>

            <Popover
                open={showRealeaseCustomerPopover}
                anchorEl={customerReleaseButtonAnchor.current}
                transitionDuration={300}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            >
                <div className="p-5 max-w-lg">

                    <p className="text-sm font-medium text-gray-900">Comentário para cliente?</p>

                    <p className="mt-1 text-sm text-gray-500">
                        Para que seu comentário fique disponível para o cliente é necessário que você libere manualmente. Deseja liberar o comentário para o cliente?
                    </p>

                    <div className="mt-4 flex space-x-2">
                        <Button
                            onClick={handleConfirmEnableComment}
                            color='primary'
                            variant='contained'
                            disabled={isReleasingCommentToCustomer}
                        >
                            <CircularProgress
                                size={20}
                                style={{ display: isReleasingCommentToCustomer ? 'block' : 'none', marginRight: 10 }}
                            />
                            Sim
                        </Button>
                        <Button
                            onClick={() => setShowRealeaseCustomerPopover(false)}
                            variant='contained'
                            disabled={isReleasingCommentToCustomer}
                        >
                            Não
                        </Button>
                    </div>

                </div>
            </Popover>
        </ChatMessage>
    );
}

function Chat({ messages, onRefresh, isLoading, showLastCommentCustomerRelasePopover, setShowLastCommentCustomerRelasePopover }) {

    const isLastComment = (index) => index === messages.length - 1;

    if (isLoading)
        return (
            <LoadingProgress />
        )

    if (!messages.length)
        return (
            <Alert severity='info'>
                Ainda não há comentários para visualizar.
            </Alert>
        )

    return (
        <Grid container direction='column' style={{ gap: 14, minWidth: 0 }}>
            {(messages || []).map((item, i) => (
                <Grid item key={item.id || `${item.created}-${i}`} style={{ minWidth: 0 }}>
                    {(i === 0 || new Date(item.created).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
                        !== new Date(messages[i - 1].created).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })) && (
                        <Typography align="center" variant="caption" color="textSecondary"
                            style={{ display: 'block', marginBottom: 12 }}>
                            {new Date(item.created).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                        </Typography>
                    )}
                    <ChatMessageComponent
                        comment={item}
                        refreshComments={onRefresh}
                        position={loginService.getUserAuthentication().id == item.senderUserId ? 'right' : 'left'}
                        showRealeaseCustomerPopover={isLastComment(i) && showLastCommentCustomerRelasePopover}
                        setShowRealeaseCustomerPopover={setShowLastCommentCustomerRelasePopover}
                    />
                </Grid>
            ))}
        </Grid>
    );
}

export default Chat;
